import React from "react";
import moment from "moment";

const getBgColor = (index) => {
  const colors = [
    "#1f2937", // slate-800
    "#374151", // gray-700
    "#111827", // almost black
    "#0f172a", // navy dark
  ];
  return colors[index % colors.length];
};

const CustomEventOrdering = ({ event }) => {
  // console.log({ event });

  const duration = event.totalTimeService ?? "—"; // mins
  const staff = event.staffName || event.selectedProfessional?.name || "—";
  const bookingtype = event.bookingtype || "-";
  const isPast = moment(event.end).isBefore(moment());
  const isCancelled =
    event.bookingStatus === "cancelled" ||
    event.booking?.status === "cancelled";

  const services = (event.orderItems || []).map(
    (i) => i?.name || i?.title || "Untitled"
  );

  const customer =
    event.customer ||
    event.user?.displayName ||
    event.customer_info?.name ||
    "—";

  const phone = event.user?.phone || event.customer_info?.mobile || "—";

  const totalPrice = event.totalPrice ?? "—";
  const message = event.customer_info?.message || "";

  const paymentMethod = event.payments?.paymentMethod || "—";
  const paymentStatus = event.payments?.status || "pending";

  const orderingMethod = event.orderingMethod || "—";
  const deliveryStatus = event.delivery?.status || "—";

  const initial = customer?.charAt(0)?.toUpperCase() || "?";

  function hexToRgba(hex, opacity) {
    let bigint = parseInt(hex.replace("#", ""), 16);
    let r = (bigint >> 16) & 255;
    let g = (bigint >> 8) & 255;
    let b = bigint & 255;
    return `rgba(${r},${g},${b},${opacity})`;
  }

  const backgroundColor = isCancelled
    ? "#f87171"
    : isPast
    ? "#d1d5db"
    : "#f3f4f6";

  const borderColor = isCancelled ? "#b91c1c" : isPast ? "#9ca3af" : "#9ca3af";

  const statusLabel = isCancelled
    ? "Cancelled"
    : isPast
    ? "Completed"
    : "Service";

  const tooltipHTML = `
    <div style="
      width: 400px;
      border: 1px solid ${borderColor};
      border-radius: 10px;
      background-color: ${hexToRgba(backgroundColor, 1)};
      color: #1f2937;
      font-family: sans-serif;
      overflow: hidden;
      margin: 3px;
    ">
      <div style="
        background: ${borderColor};
        color: #fff;
        padding: 10px 14px;
        font-size: 13px;
        font-weight: bold;
        display: flex;
        justify-content: space-between;
        align-items: center;
      ">
        ${moment(event.start).format("h:mm A")} – ${moment(event.end).format(
    "h:mm A"
  )}
        <span>${statusLabel}</span>
      </div>

      <div style="padding: 14px;">
        <div style="display:flex; align-items:center; gap:12px;">
          <div style='width:40px; height:40px; border-radius:50%; background:#334155; display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:16px; color:#facc15;'>
            ${initial}
          </div>
          <div style="flex-grow:1;">
            <div style="font-weight:600; text-transform: uppercase">
              ${customer}
              ${
                bookingtype === "walk-in"
                  ? `<span style="font-size:10px; background:#3b82f6; color:white; padding:2px 6px; border-radius:4px;">Walk-in</span>`
                  : `<span style="font-size:10px; background:#10b981; color:white; padding:2px 6px; border-radius:4px;">Online</span>`
              }
            </div>
            <div style="font-size:12px; color:#222;">${phone}</div>
          </div>
        </div>

        <div style="margin-top:12px; font-size:13px;">
             ${services
               .map(
                 (srv) => `
              <div style="margin-bottom:4px;">
                <strong>${srv}</strong>
              </div>
            `
               )
               .join("")}
          <div style="font-size:11px; color:#94a3b8;">${staff} • ${duration} min</div>
        </div>

        <hr style="margin:12px 0; border-color:#475569;" />

        <div style="font-size:12px; color:#222;">💲 Total: <strong>${totalPrice}</strong></div>
        <div style="font-size:12px; color:#222;">⏱ Duration: ${duration} mins</div>
        <div style="font-size:12px; color:${
          paymentStatus === "paid"
            ? "green"
            : paymentStatus === "failed"
            ? "red"
            : "orange"
        };">💳 ${paymentMethod} – ${paymentStatus}</div>
<div style="font-size:12px; color:#222;">🛍 Ordering: <strong>${orderingMethod}</strong></div>
<div style="font-size:12px; color:#222;">🚚 Delivery: ${deliveryStatus}</div>
        ${
          message
            ? `<div style="margin-top:8px; font-size:12px; color:#facc15; background:#334155; padding:6px 10px; border-radius:6px; display:flex; align-items:center; gap:6px;">
                ⚠️ ${message}
              </div>`
            : ""
        }
      </div>
    </div>
  `;

  // TEMP slot (hover only)
  if (event.temp) {
    return (
      <div className="flex items-center justify-start h-full w-full text-yellowlight text-[10px] ml-2">
        {event.title}
      </div>
    );
  }

  // Break event
  if (event.isBreak) {
    return (
      <div className="flex flex-col items-start justify-center h-full w-full text-dry text-[12px] ml-2">
        <p className="uppercase text-red-500">{event.title}</p>
        <div className="mt-1">
          {moment(event.start).format("h:mm A")} –{" "}
          {moment(event.end).format("h:mm A")}
        </div>
      </div>
    );
  }

  // Busy time
  if (event.isBusy) {
    return (
      <div className="flex flex-col items-start justify-center h-full w-full text-dry text-[12px] ml-2 font-semibold">
        <p className="uppercase text-red-500">{event.title}</p>
        <div className="mt-1">
          {moment(event.start).format("h:mm A")} –{" "}
          {moment(event.end).format("h:mm A")}
        </div>
      </div>
    );
  }

  // Inside your return (main appointment UI)
  return (
    <>
      <div
        className="flex flex-col w-full h-full "
        data-tooltip-id="event-tooltip"
        data-tooltip-html={tooltipHTML}
        style={{
          fontFamily: "monospace",
          fontSize: "14px",
          lineHeight: "1.2",
        }}
      >
        {/* Header like Side · Bag · R02-01 */}
        <div className="flex justify-between items-center px-2 py-1 border-b border-black bg-white font-bold text-xs">
          <span className="capitalize">{customer}</span>
          <span
            className={`text-[10px] px-2 py-[1px] rounded ${
              bookingtype === "walk-in"
                ? "bg-blue-500 text-white"
                : "bg-green-500 text-white"
            }`}
          >
            {bookingtype === "walk-in" ? "Walk-in" : "Online"}
          </span>
        </div>

        {/* Services grouped by category */}
        <div className="flex-1 p-2">
          <div className="mb-2">
            {services.slice(0, 3).map((srv, i) => (
              <div key={i} className="mb-[2px]">
                {srv}
              </div>
            ))}
          </div>

          <div className="mb-2">
            {services.slice(3, 6).map((srv, i) => (
              <div key={i} className="mb-[2px]">
                {srv}
              </div>
            ))}
          </div>
          <hr className="border-black my-1" />

          <div className="mb-2">
            {services.slice(6).map((srv, i) => (
              <div key={i} className="mb-[2px]">
                {srv}
              </div>
            ))}
          </div>

          <div
            className={` mb-2 ${
              paymentStatus === "paid"
                ? "text-green-600"
                : paymentStatus === "failed"
                ? "text-red-600"
                : "text-yellow-700"
            }`}
          >
            💳 {paymentMethod} – {paymentStatus}
          </div>
          <div className=" text-gray-700 mb-2">
            🛍 {orderingMethod} at {moment(event.start).format("h:mm A")}
          </div>
          <div className=" text-gray-700 mb-2">🚚 {deliveryStatus}</div>
          {/* Optional message */}
          {message && (
            <div className="text-xs leading-tight mt-1">⚠️ {message}</div>
          )}
          {/* Mc donald */}
        </div>

        {/* Footer like Paid · 799 */}
        <div className="flex flex-col justify-between items-center bg-red-600 text-white px-2 py-1 text-sm font-bold">
          <div className="flex justify-between">
            {" "}
            <span>{paymentStatus === "paid" ? "Paid" : paymentStatus}</span>
            <span>${totalPrice}</span>
          </div>
          <div className="flex justify-between w-full">
            <span>⏱ </span>
            <span>{duration} mins</span>
          </div>
        </div>
      </div>

      <div
        className="p-2 text-sm w-full"
        data-tooltip-id="event-tooltip"
        data-tooltip-html={tooltipHTML}
        style={{
          borderRadius: "6px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          fontSize: "13px",
          fontFamily: "sans-serif",
          height: "auto", // let it grow based on service list
          minHeight: "40px", // keep small events visible
        }}
      >
        {/* Customer name + type */}
        <div className="font-bold leading-tight flex items-center gap-2 mb-1">
          <span className="capitalize">{customer}</span>
          <span
            className={`text-[10px] px-2 py-[1px] rounded ${
              bookingtype === "walk-in"
                ? "bg-blue-500 text-white"
                : "bg-green-500 text-white"
            }`}
          >
            {bookingtype === "walk-in" ? "Walk-in" : "Online"}
          </span>
        </div>

        {/* Services list (vertical) */}
        <div className="flex flex-col gap-[2px] mb-1">
          {services.map((srv, idx) => (
            <div key={idx} className="text-xs leading-tight truncate">
              {srv}
            </div>
          ))}
        </div>

        {/* Time */}
        <div className="text-[10px] text-gray-700">
          {moment(event.start).format("h:mm A")} –{" "}
          {moment(event.end).format("h:mm A")}
        </div>

        {/* Price + duration */}
        <div className="text-[10px] text-gray-700">💲 {totalPrice}</div>
        <div className="text-[10px] text-gray-700">⏱ {duration} mins</div>

        {/* Payment */}
        <div
          className={`text-[10px] ${
            paymentStatus === "paid"
              ? "text-green-600"
              : paymentStatus === "failed"
              ? "text-red-600"
              : "text-yellow-600"
          }`}
        >
          💳 {paymentMethod} – {paymentStatus}
        </div>
        <div className="text-[10px] text-gray-700">🛍 {orderingMethod}</div>
        <div className="text-[10px] text-gray-700">🚚 {deliveryStatus}</div>
        {/* Optional message */}
        {message && (
          <div className="text-xs leading-tight mt-1">⚠️ {message}</div>
        )}
        {/* Mc donald */}
      </div>
    </>
  );
};

export default CustomEventOrdering;
